import AnimatedLoader from '@/components/AnimatedLoader'
import UserDashboard from '@/screens/user/UserDashboard'
import InspectionReport from '@/screens/user/inspections'
import LoginForm from '@/screens/auth/Login'
import RegisterForm from '@/screens/auth/RegisterForm'
import React, { Suspense, useEffect } from 'react'
import { Route, Routes, Navigate, Link } from 'react-router-dom'
import ProtectedRoute from './ProtectedRoute'
import { useAuth } from '@/hooks/useAuth'
import ForgetPassword from '@/screens/auth/ForgetPassword'
import InspectionList from '@/screens/user/inspections/InspectionList'
import FormBuilder from '@/screens/admin/modulecreator'
import TextRecommendation from '@/screens/admin/TextRecommendation'
import FeedbackList from '@/screens/admin/FeedbackList'
import OwnerUserList from '@/screens/owner/OwnerUserManagement'
import RootOwnerDashboard from '@/screens/owner/Dashboard'
import OwnerShipManagement from '@/screens/admin/Dashboard'
import SubscriptionManagement from '@/screens/admin/SubscriptionManagement'
import SpotFlashLanding from '@/screens/Landing'
import Header from '@/components/Header'
import Reports from '@/screens/user/Reports'
import VerifyEmail from '@/screens/auth/VerifyEmail'
import { useDispatch, useSelector } from 'react-redux'
import { useReportAsync } from '@/stores/slice/authSlice'
import ReportManager from '@/screens/user/Reports'
import ReportManagement from '@/screens/admin/Reports/EventManagement'
import ReportEvents from '@/screens/admin/Reports'

const AppRoutes = () => {
    const { isAuthenticated, role, isLoading } = useAuth();
    const dispatch = useDispatch()

     if (isLoading) {
        return <AnimatedLoader />;
    }

    return (
        <Suspense fallback={<AnimatedLoader />}>
            <Routes>
                {/* Root Route - Handle authentication redirects */}
                <Route
                    path="/"
                    element={
                        isAuthenticated ? (
                            role === 'ADMIN' ? (
                                <Navigate to="/admin/dashboard" replace />
                            ) : role === 'OWNER' ? (
                                <Navigate to="/manager/dashboard" replace />
                            ) : role === 'USER' ? (
                                <Navigate to="/user/dashboard" replace />
                            ) : (
                                <Navigate to="/unauthorized" replace />
                            )
                        ) : (
                            <>
                                <Header />
                                <main className="min-h-screen w-full bg-gray-50 md:mt-15">
                                    <SpotFlashLanding />
                                </main>
                            </>
                        )
                    }
                />

                {/* Public Routes */}
                <Route
                    path="/login"
                    element={
                        isAuthenticated ? (
                            role === 'ADMIN' ? (
                                <Navigate to="/admin/dashboard" replace />
                            ) : role === 'OWNER' ? (
                                <Navigate to="/manager/dashboard" replace />
                            ) : (
                                <Navigate to="/user/dashboard" replace />
                            )
                        ) : (
                            <LoginForm />
                        )
                    }
                />
                <Route
                    path="/verify"
                    element={
                        <VerifyEmail />
                    }
                />



                <Route
                    path="/forgetpassword"
                    element={
                        isAuthenticated ? (
                            role === 'ADMIN' ? (
                                <Navigate to="/admin/dashboard" replace />
                            ) : role === 'OWNER' ? (
                                <Navigate to="/manager/dashboard" replace />
                            ) : (
                                <Navigate to="/user/dashboard" replace />
                            )
                        ) : (
                            <ForgetPassword />
                        )
                    }
                />

                <Route
                    path="/register/:email"
                    element={
                        isAuthenticated ? (
                            role === 'ADMIN' ? (
                                <Navigate to="/admin/dashboard" replace />
                            ) : role === 'OWNER' ? (
                                <Navigate to="/manager/dashboard" replace />
                            ) : (
                                <Navigate to="/user/dashboard" replace />
                            )
                        ) : (
                            <RegisterForm />
                        )
                    }
                />
                <Route
                    path="/register"
                    element={
                        <RegisterForm />
                    }
                />
                {/* ===== ADMIN Routes ===== */}
                <Route
                    path="/admin/dashboard"
                    element={
                        <ProtectedRoute requiredRole="ADMIN">
                            <OwnerShipManagement />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/admin/report-manager"
                    element={
                        <ProtectedRoute requiredRole="ADMIN">
                            <ReportEvents />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/admin/subscription"
                    element={
                        <ProtectedRoute requiredRole="ADMIN">
                            <SubscriptionManagement />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/admin/recommdation"
                    element={
                        <ProtectedRoute requiredRole="ADMIN">
                            <TextRecommendation />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/admin/module-builder"
                    element={
                        <ProtectedRoute requiredRole="ADMIN">
                            <FormBuilder />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/admin/feedbacks"
                    element={
                        <ProtectedRoute requiredRole="ADMIN">
                            <FeedbackList />
                        </ProtectedRoute>
                    }
                />

                {/* ===== OWNER Routes ===== */}
                <Route
                    path="/manager/dashboard"
                    element={
                        <ProtectedRoute requiredRole="OWNER">
                            <RootOwnerDashboard />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/manager/users"
                    element={
                        <ProtectedRoute requiredRole="OWNER">
                            <OwnerUserList />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/manager/inspections"
                    element={
                        <ProtectedRoute requiredRole="OWNER">
                            <InspectionReport />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/manager/reports/:reportname"
                    element={
                        <ProtectedRoute requiredRole="OWNER">
                            <Reports />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/manager/reports"
                    element={
                        <ProtectedRoute requiredRole="OWNER">
                            <Reports />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/manager/inspectionlist"
                    element={
                        <ProtectedRoute requiredRole="OWNER">
                            <InspectionList />
                        </ProtectedRoute>
                    }
                />



                {/* ===== USER Routes ===== */}
                <Route
                    path="/user/dashboard"
                    element={
                        <ProtectedRoute requiredRole="USER">
                            <UserDashboard />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="/user/inspections"
                    element={
                        <ProtectedRoute requiredRole="USER">
                            <InspectionReport />
                        </ProtectedRoute>
                    }
                />
  
                <Route
                    path="/user/inspectionlist"
                    element={
                        <ProtectedRoute requiredRole="USER">
                            <InspectionList />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/user/reports/:reportname"
                    element={
                        <ProtectedRoute requiredRole="USER">
                            <Reports />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/user/reports"
                    element={
                        <ProtectedRoute requiredRole="USER">
                            <Reports />
                        </ProtectedRoute>
                    }
                />


                {/* Unauthorized Page */}
                <Route
                    path="/unauthorized"
                    element={
                        <div className="flex items-center justify-center min-h-screen bg-gray-50">
                            <div className="text-center p-8 bg-white rounded-lg shadow-md max-w-md mx-4">
                                <div className="text-red-500 text-6xl mb-4">🚫</div>
                                <h1 className="text-3xl font-bold text-red-600 mb-4">
                                    Unauthorized Access
                                </h1>
                                <p className="text-gray-600 mb-6">
                                    You don't have permission to access this page.
                                    Please contact your administrator if you think this is an error.
                                </p>
                                <div className="space-y-3">
                                    <button
                                        onClick={() => window.history.back()}
                                        className="w-full px-6 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                                    >
                                        Go Back
                                    </button>
                                    <Link
                                        to={isAuthenticated ?
                                            (role === 'ADMIN' ? '/admin/dashboard' :
                                                role === 'OWNER' ? '/manager/dashboard' :
                                                    '/user/dashboard') :
                                            '/login'
                                        }
                                        className="block w-full px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-center"
                                    >
                                        Go to Dashboard
                                    </Link>
                                </div>
                            </div>
                        </div>
                    }
                />

                {/* 404 Not Found */}
                <Route
                    path="*"
                    element={
                        <div className="flex items-center justify-center min-h-screen bg-gray-50">
                            <div className="text-center p-8 bg-white rounded-lg shadow-md max-w-md mx-4">
                                <div className="text-gray-400 text-6xl mb-4">🔍</div>
                                <h1 className="text-3xl font-bold text-gray-800 mb-4">
                                    404 - Page Not Found
                                </h1>
                                <p className="text-gray-600 mb-6">
                                    The page you're looking for doesn't exist or has been moved.
                                </p>
                                <div className="space-y-3">
                                    <button
                                        onClick={() => window.history.back()}
                                        className="w-full px-6 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                                    >
                                        Go Back
                                    </button>
                                    <Link
                                        to={isAuthenticated ?
                                            (role === 'ADMIN' ? '/admin/dashboard' :
                                                role === 'OWNER' ? '/manager/dashboard' :
                                                    '/user/dashboard') :
                                            '/login'
                                        }
                                        className="block w-full px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-center"
                                    >
                                        Go to Dashboard
                                    </Link>
                                </div>
                            </div>
                        </div>
                    }
                />
            </Routes>
        </Suspense>
    )
}

export default AppRoutes